import { Stack } from '@/core/blocks';
import { HtmlMarkup, Skeleton, Typography } from '@/core/ui';
import { cn } from '@/lib';
import { useAppSelector } from '@/store';
import { API } from '@/store/api';
import { getFormattedDate } from '@/utils';
import { useTranslation } from 'react-i18next';

export const PlayerDetail = () => {
  const president = useAppSelector((s) => s.history.president);
  const { i18n, t } = useTranslation();
  const { data, error, isFetching, isLoading } = API.historyApi.useGetPresidentDetailQuery(
    {
      presidentId: president?.id as number,
      locale: i18n.language,
    },
    { skip: !president || !i18n.language },
  );

  if (error) {
    return <p>Oh no, there was an error</p>;
  }

  if (isFetching || isLoading || !data) {
    return (
      <div className='bg-linear-to-r/srgb from-olive/90 via-apple to-apple rounded-2xl h-full w-full'>
        <div className={cn('flex flex-row items-center h-full w-full')}>
          <div className='flex flex-col w-full items-center justify-between space-y-10 basis-5/12 p-[105px]'>
            <Skeleton className='rounded-full w-[361px] h-[361px]' />
            <div className='flex flex-col space-y-8 items-center'>
              <Skeleton className='h-[80px] w-64' />
              <Skeleton className='h-[38px] w-32' />
            </div>
          </div>
          <div className='w-full basis-7/12 pl-0 p-[15px]'>
            <div className='flex flex-col space-y-8'>
              <Skeleton className='h-[50px] w-96' />
              <Skeleton className='h-[275px] w-[664px]' />
            </div>
            {/* <div className='flex flex-col space-y-[38px]'>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className='flex justify-between items-center'>
                  <div className='flex space-x-6'>
                    <Stack upperClass='w-[10px] h-[20px]' lowerClass='w-[10px] h-[16px]' />
                    <Skeleton className='h-[50px] w-72' />
                  </div>
                  <Skeleton className='h-[50px] w-20' />
                </div>
              ))}
            </div> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-linear-to-r/srgb from-olive/90 via-apple to-apple rounded-2xl h-full w-full'>
      <div className={cn('flex flex-row items-center h-full w-full bg-player bg-no-repeat')}>
        <div className='flex flex-col w-full items-center justify-between space-y-10 basis-5/12 p-[105px]'>
          {data?.profile_image.path ? (
            <img
              alt={data?.profile_image.caption || ''}
              src={data?.profile_image.path}
              className='bg-chalk rounded-full w-[361px] h-[361px] object-cover border-[5px] border-white shadow-md'
            />
          ) : (
            <img
              alt='player'
              src='/default.png'
              className='bg-chalk rounded-full w-[361px] h-[361px] object-cover'
            />
          )}
          <div className='max-w-[267px] flex flex-col space-y-8 text-center'>
            <Typography className='text-[55px] font-bold leading-[52.25px] tracking-[3.85px] uppercase'>
              {data?.name}
            </Typography>
            <div className='flex flex-col space-y-2'>
              {data?.is_current && (
                <Typography className='text-[32px] font-semibold leading-[32px] tracking-[1.6px] uppercase'>
                  {t('history.member.president_text')}
                </Typography>
              )}
            </div>
          </div>
        </div>
        <div className='w-full basis-7/12 flex flex-col space-y-12 justify-between pl-0 p-[105px]'>
          <div className='flex flex-col space-y-8'>
            <div className='max-w-2xl'>
              <Typography className='text-[45px] font-semibold leading-[42.75px] tracking-[3.15px] uppercase'>
                {/* {data.competition.name} */}
                {getFormattedDate(data.start_date || new Date(), 'YYYY')}-
                {data.is_current
                  ? t('history.member.current')
                  : getFormattedDate(data.end_date || new Date(), 'YYYY')}
              </Typography>
            </div>
            <div className='flex flex-col max-w-[664px] space-y-7'>
              <HtmlMarkup className='text-2xl leading-[27.6px] tracking-[1.2px]'>
                {data?.description}
              </HtmlMarkup>
            </div>
          </div>
          <div className='flex flex-col space-y-[38px]'>
            {data?.extra_info && Array.isArray(data.extra_info) && data.extra_info.length > 0 ? (
              data.extra_info.map((i) => (
                <div key={i.title} className='flex justify-between items-center'>
                  <div className='flex space-x-6'>
                    <Stack upperClass='w-[10px] h-[23px]' lowerClass='w-[10px] h-[13px]' />
                    <Typography className='text-[45px] font-semibold leading-[45px] tracking-[3.15px] uppercase'>
                      {i.title}
                    </Typography>
                  </div>
                  <Typography className='text-[50px] font-bold leading-[50px] tracking-[3.5px] uppercase'>
                    {i.value}
                  </Typography>
                </div>
              ))
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
